import axios from 'axios';
import { HostPlatform, JIRA, Monday, Azure } from '../config/hostPlatform';
import { getMyself } from './users';
import { FirebaseApp, initializeApp } from 'firebase/app';
import {
  Firestore,
  connectFirestoreEmulator,
  getFirestore,
} from 'firebase/firestore';
import {
  Functions,
  connectFunctionsEmulator,
  getFunctions,
} from 'firebase/functions';
import {
  connectAuthEmulator,
  getAuth,
  signInWithCustomToken,
} from 'firebase/auth';
import {
  fetchAndActivate,
  getRemoteConfig,
  setLogLevel,
} from 'firebase/remote-config';

export type Subscription = {
  isActive: boolean;
  type?: 'trial' | 'paid';
  endsAt?: Date;
  startsAt?: Date;
  nextInvoice?: Date;
  status?: string;
};

export type AppInitializationContext = {
  firebaseToken?: string;
  subscription: Subscription;
};

let userId: string;
let tenantId: string;
let boardId: string;
let boardViewId: string;
let subscription: Subscription;

const isProduction = process.env.NODE_ENV === 'production';
const shouldUseEmulators =
  process.env.REACT_APP_FIREBASE_USE_EMULATORS === 'true';

const initializeAzureTenant = async (hostPlatform: string) => {
  const response = await getFirebaseToken(hostPlatform);

  const myself = await getMyself();
  userId = myself.id;
  tenantId = response.tenantId;
  return {
    firebaseToken: response.firebaseToken,
    subscription: response.subscription,
  };
};

const initializeStandardTenant = async (hostPlatform) => {
  const response = await getFirebaseToken(hostPlatform);
  userId = response.userId;
  tenantId = response.tenantId;
  return {
    firebaseToken: response.firebaseToken,
    subscription: response.subscription,
  };
};

const Initializers = new Map<
  'jira' | 'monday' | 'azure',
  (hostPlatform: string) => Promise<AppInitializationContext>
>([
  [JIRA, initializeStandardTenant],
  [Monday, initializeStandardTenant],
  [Azure, initializeAzureTenant],
]);

const getFirebaseToken = async (hostPlatform) => {
  const url = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/createToken`;
  const response = await axios.post(
    url,
    {
      slug: hostPlatform.context.slug,
      accountId: hostPlatform.context.accountId,
      platform: hostPlatform.id,
    },
    { headers: { Authorization: `Bearer ${hostPlatform.token}` } }
  );

  // FIXME this workaround is necessary for Azure DevOps because the user ID present in the token doesn't match the current user ID
  // see https://developercommunity.visualstudio.com/t/user-id-from-sdkgetapptoken-doesnt-match-the-user/1610412
  if (HostPlatform.isAzure()) {
    const myself = await getMyself();
    userId = myself.id;
  } else {
    userId = response.data.userId;
  }
  tenantId = response.data.tenantId;
  return response.data;
};

function initializeAuth(app: FirebaseApp) {
  const auth = getAuth(app);
  auth.settings.appVerificationDisabledForTesting = !isProduction;
  if (shouldUseEmulators && process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST) {
    console.warn(
      'Using local emulator AUTH "%s"',
      process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST
    );
    connectAuthEmulator(
      auth,
      process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_HOST
    );
  }
  return auth;
}

function initializeFirestore(app: FirebaseApp): Firestore {
  const firestore = getFirestore(app);
  if (shouldUseEmulators && process.env.REACT_APP_FIRESTORE_EMULATOR_PORT) {
    console.warn(
      'Using local emulator FIRESTORE "%s:%s"',
      process.env.REACT_APP_FIRESTORE_EMULATOR_HOST,
      process.env.REACT_APP_FIRESTORE_EMULATOR_PORT
    );
    connectFirestoreEmulator(
      firestore,
      process.env.REACT_APP_FIRESTORE_EMULATOR_HOST as string,
      process.env.REACT_APP_FIRESTORE_EMULATOR_PORT as unknown as number
    );
  }

  return firestore;
}

function initializeFunctions(app: FirebaseApp): Functions {
  const functions = getFunctions(app);
  if (shouldUseEmulators && process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT) {
    console.warn(
      'Using local emulator FUNCTIONS "%s:%s"',
      process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST,
      process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT
    );
    connectFunctionsEmulator(
      functions,
      process.env.REACT_APP_FUNCTIONS_EMULATOR_HOST as string,
      process.env.REACT_APP_FUNCTIONS_EMULATOR_PORT as unknown as number
    );
  }

  return functions;
}

const initializeRemoteConfig = async (app: FirebaseApp) => {
  const remoteConfig = getRemoteConfig(app);
  remoteConfig.defaultConfig = {
    FEATURE_TEMPLATES: true,
    FEATURE_AZURE_TRIAL: true,
  };
  setLogLevel(remoteConfig, isProduction ? 'silent' : 'debug');
  if (!isProduction) {
    console.warn('Remote config running in dev mode');
    remoteConfig.settings.minimumFetchIntervalMillis = 0;
  }
  await fetchAndActivate(remoteConfig).catch(() => {
    // The error is supressed if Remote Config can't be loaded.
    // For example, it might be that the client is running in Incognito mode, where Remote Config doesn't work.
    console.warn('Remote config could not be loaded');
  });

  return remoteConfig;
};

let firebaseApp: FirebaseApp | undefined;
const initializeFirebase = async (firebaseToken) => {
  // eslint-disable-next-line import/no-named-as-default-member
  firebaseApp = initializeApp({
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  });
  initializeFirestore(firebaseApp);
  initializeFunctions(firebaseApp);
  const auth = initializeAuth(firebaseApp);
  await signInWithCustomToken(auth, firebaseToken);
  await initializeRemoteConfig(firebaseApp);
};

const firebaseInit = async (
  hostPlatform: any
): Promise<{
  appInitializationContext: AppInitializationContext;
}> => {
  boardId = hostPlatform.context.boardId;
  boardViewId = hostPlatform.context.boardViewId;

  const initializer = Initializers.get(hostPlatform.id);
  if (!initializer) {
    throw new Error(`Platform "${hostPlatform.id}" not supported`);
  }

  const appInitializationContext = await initializer(hostPlatform);
  subscription = appInitializationContext.subscription;

  await initializeFirebase(appInitializationContext.firebaseToken);
  return { appInitializationContext };
};

const getUserId = () => userId;

const getTenantId = () => tenantId;

const getBoardId = () => boardId;

const getBoardViewId = () => boardViewId;

const getSubscription = () => subscription;

export {
  firebaseInit,
  getUserId,
  getTenantId,
  getBoardId,
  getBoardViewId,
  getSubscription,
};
